import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';

interface FiltrosFlota {
  busqueda: string;
  tipo: string[];
  estado: string[];
  conductor: string;
  fechaInicio: string;
  fechaFin: string;
  capacidadMin: number;
  capacidadMax: number;
  consumoMin: number;
  consumoMax: number;
  añoMin: number;
  añoMax: number;
  marca: string[];
  mantenimientoProximo: boolean;
  sinConductor: boolean;
}

interface ModalFiltrosFlotaProps {
  isOpen: boolean;
  onClose: () => void;
  filtrosActuales: FiltrosFlota;
  onAplicarFiltros: (filtros: FiltrosFlota) => void;
}

const ModalFiltrosFlota: React.FC<ModalFiltrosFlotaProps> = ({
  isOpen,
  onClose,
  filtrosActuales,
  onAplicarFiltros
}) => {
  // Estados del formulario
  const [filtros, setFiltros] = useState<FiltrosFlota>(filtrosActuales);
  const [aplicando, setAplicando] = useState(false);

  // Opciones disponibles
  const tiposVehiculo = [
    { value: 'camion', label: 'Camión' },
    { value: 'camioneta', label: 'Camioneta' },
    { value: 'furgon', label: 'Furgón' },
    { value: 'motocicleta', label: 'Motocicleta' }
  ];

  const estadosVehiculo = [
    { value: 'disponible', label: 'Disponible' },
    { value: 'en_ruta', label: 'En Ruta' },
    { value: 'mantenimiento', label: 'Mantenimiento' },
    { value: 'fuera_servicio', label: 'Fuera de Servicio' }
  ];

  const marcasVehiculo = [
    { value: 'chevrolet', label: 'Chevrolet' },
    { value: 'ford', label: 'Ford' },
    { value: 'nissan', label: 'Nissan' },
    { value: 'toyota', label: 'Toyota' },
    { value: 'hyundai', label: 'Hyundai' },
    { value: 'volkswagen', label: 'Volkswagen' },
    { value: 'mercedes', label: 'Mercedes-Benz' },
    { value: 'volvo', label: 'Volvo' }
  ];

  // Cargar filtros actuales cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setFiltros(filtrosActuales);
    }
  }, [isOpen, filtrosActuales]);

  // Manejar cambios en arrays (checkboxes múltiples)
  const manejarCambioArray = (campo: keyof FiltrosFlota, valor: string, checked: boolean) => {
    setFiltros(prev => {
      const arrayActual = prev[campo] as string[];
      if (checked) {
        return {
          ...prev,
          [campo]: [...arrayActual, valor]
        };
      } else {
        return {
          ...prev,
          [campo]: arrayActual.filter(item => item !== valor)
        };
      }
    });
  };

  // Manejar cambios simples
  const manejarCambio = (campo: keyof FiltrosFlota, valor: any) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    const filtrosVacios: FiltrosFlota = {
      busqueda: '',
      tipo: [],
      estado: [],
      conductor: '',
      fechaInicio: '',
      fechaFin: '',
      capacidadMin: 0,
      capacidadMax: 50000,
      consumoMin: 0,
      consumoMax: 50,
      añoMin: 1990,
      añoMax: new Date().getFullYear(),
      marca: [],
      mantenimientoProximo: false,
      sinConductor: false
    };
    setFiltros(filtrosVacios);
  };

  // Aplicar filtros
  const aplicarFiltros = async () => {
    setAplicando(true);
    
    try {
      // Simulación de procesamiento
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onAplicarFiltros(filtros);
      onClose();
    } catch (error) {
      console.error('Error al aplicar filtros:', error);
    } finally {
      setAplicando(false);
    }
  };

  // Contar filtros activos
  const contarFiltrosActivos = (): number => {
    let count = 0;
    
    if (filtros.busqueda) count++;
    if (filtros.tipo.length > 0) count++;
    if (filtros.estado.length > 0) count++;
    if (filtros.conductor) count++;
    if (filtros.fechaInicio || filtros.fechaFin) count++;
    if (filtros.capacidadMin > 0 || filtros.capacidadMax < 50000) count++;
    if (filtros.consumoMin > 0 || filtros.consumoMax < 50) count++;
    if (filtros.añoMin > 1990 || filtros.añoMax < new Date().getFullYear()) count++;
    if (filtros.marca.length > 0) count++;
    if (filtros.mantenimientoProximo) count++;
    if (filtros.sinConductor) count++;
    
    return count;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Filtros Avanzados de Flota" size="xl">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <FunnelIcon className="h-6 w-6 mr-2 text-blue-600" />
              Filtros Avanzados
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {contarFiltrosActivos()} filtro(s) activo(s)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Formulario de filtros */}
        <div className="space-y-6 max-h-96 overflow-y-auto">
          {/* Búsqueda general */}
          <div>
            <label htmlFor="modal-filtros-flota-busqueda" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Búsqueda General
            </label>
            <input
              id="modal-filtros-flota-busqueda"
              name="filtros.busqueda"
              type="text"
              value={filtros.busqueda}
              onChange={(e) => manejarCambio('busqueda', e.target.value)}
              placeholder="Buscar por placa, marca, modelo..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Tipo de vehículo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Tipo de Vehículo
            </label>
            <div className="grid grid-cols-2 gap-2">
              {tiposVehiculo.map((tipo) => (
                <label key={tipo.value} htmlFor={`modal-filtros-flota-tipo-${tipo.value}`} className="flex items-center">
                  <input
                    id={`modal-filtros-flota-tipo-${tipo.value}`}
                    name="filtros.tipo[]"
                    type="checkbox"
                    checked={filtros.tipo.includes(tipo.value)}
                    onChange={(e) => manejarCambioArray('tipo', tipo.value, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{tipo.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Estado
            </label>
            <div className="grid grid-cols-2 gap-2">
              {estadosVehiculo.map((estado) => (
                <label key={estado.value} htmlFor={`modal-filtros-flota-estado-${estado.value}`} className="flex items-center">
                  <input
                    id={`modal-filtros-flota-estado-${estado.value}`}
                    name="filtros.estado[]"
                    type="checkbox"
                    checked={filtros.estado.includes(estado.value)}
                    onChange={(e) => manejarCambioArray('estado', estado.value, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{estado.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Marca */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Marca
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {marcasVehiculo.map((marca) => (
                <label key={marca.value} htmlFor={`modal-filtros-flota-marca-${marca.value}`} className="flex items-center">
                  <input
                    id={`modal-filtros-flota-marca-${marca.value}`}
                    name="filtros.marca[]"
                    type="checkbox"
                    checked={filtros.marca.includes(marca.value)}
                    onChange={(e) => manejarCambioArray('marca', marca.value, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{marca.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Rango de fechas */}
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Rango de Fechas de Registro
              </legend>
            </fieldset>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="modal-filtros-flota-fecha-inicio" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Desde</label>
                <input
                  id="modal-filtros-flota-fecha-inicio"
                  name="filtros.fechaInicio"
                  type="date"
                  value={filtros.fechaInicio}
                  onChange={(e) => manejarCambio('fechaInicio', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="modal-filtros-flota-fecha-fin" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hasta</label>
                <input
                  id="modal-filtros-flota-fecha-fin"
                  name="filtros.fechaFin"
                  type="date"
                  value={filtros.fechaFin}
                  onChange={(e) => manejarCambio('fechaFin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Capacidad de carga */}
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Capacidad de Carga (kg)
              </legend>
            </fieldset>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="modal-filtros-flota-capacidad-min" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Mínimo</label>
                <input
                  id="modal-filtros-flota-capacidad-min"
                  name="filtros.capacidadMin"
                  type="number"
                  value={filtros.capacidadMin}
                  onChange={(e) => manejarCambio('capacidadMin', parseInt(e.target.value) || 0)}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="modal-filtros-flota-capacidad-max" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Máximo</label>
                <input
                  id="modal-filtros-flota-capacidad-max"
                  name="filtros.capacidadMax"
                  type="number"
                  value={filtros.capacidadMax}
                  onChange={(e) => manejarCambio('capacidadMax', parseInt(e.target.value) || 50000)}
                  min="0"
                  placeholder="50000"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Consumo de combustible */}
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Consumo de Combustible (km/galón)
              </legend>
            </fieldset>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="modal-filtros-flota-consumo-min" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Mínimo</label>
                <input
                  id="modal-filtros-flota-consumo-min"
                  name="filtros.consumoMin"
                  type="number"
                  value={filtros.consumoMin}
                  onChange={(e) => manejarCambio('consumoMin', parseInt(e.target.value) || 0)}
                  min="0"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="modal-filtros-flota-consumo-max" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Máximo</label>
                <input
                  id="modal-filtros-flota-consumo-max"
                  name="filtros.consumoMax"
                  type="number"
                  value={filtros.consumoMax}
                  onChange={(e) => manejarCambio('consumoMax', parseInt(e.target.value) || 50)}
                  min="0"
                  placeholder="50"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Año del vehículo */}
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Año del Vehículo
              </legend>
            </fieldset>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="modal-filtros-flota-anio-min" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Desde</label>
                <input
                  id="modal-filtros-flota-anio-min"
                  name="filtros.añoMin"
                  type="number"
                  value={filtros.añoMin}
                  onChange={(e) => manejarCambio('añoMin', parseInt(e.target.value) || 1990)}
                  min="1990"
                  max={new Date().getFullYear()}
                  placeholder="1990"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="modal-filtros-flota-anio-max" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hasta</label>
                <input
                  id="modal-filtros-flota-anio-max"
                  name="filtros.añoMax"
                  type="number"
                  value={filtros.añoMax}
                  onChange={(e) => manejarCambio('añoMax', parseInt(e.target.value) || new Date().getFullYear())}
                  min="1990"
                  max={new Date().getFullYear()}
                  placeholder={new Date().getFullYear().toString()}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Conductor */}
          <div>
            <label htmlFor="modal-filtros-flota-conductor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Conductor
            </label>
            <input
              id="modal-filtros-flota-conductor"
              name="filtros.conductor"
              type="text"
              value={filtros.conductor}
              onChange={(e) => manejarCambio('conductor', e.target.value)}
              placeholder="Nombre del conductor..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Filtros especiales */}
          <div>
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Filtros Especiales
              </legend>
            </fieldset>
            <div className="space-y-2">
              <label htmlFor="modal-filtros-flota-mantenimiento" className="flex items-center">
                <input
                  id="modal-filtros-flota-mantenimiento"
                  name="filtros.mantenimientoProximo"
                  type="checkbox"
                  checked={filtros.mantenimientoProximo}
                  onChange={(e) => manejarCambio('mantenimientoProximo', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Mantenimiento próximo (próximos 30 días)
                </span>
              </label>
              
              <label htmlFor="modal-filtros-flota-sin-conductor" className="flex items-center">
                <input
                  id="modal-filtros-flota-sin-conductor"
                  name="filtros.sinConductor"
                  type="checkbox"
                  checked={filtros.sinConductor}
                  onChange={(e) => manejarCambio('sinConductor', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Sin conductor asignado
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
          <button
            onClick={limpiarFiltros}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-sm font-medium"
          >
            Limpiar todos los filtros
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={aplicarFiltros}
              disabled={aplicando}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center transition-colors disabled:opacity-50"
            >
              {aplicando ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Aplicando...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Aplicar Filtros ({contarFiltrosActivos()})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalFiltrosFlota;
